import {
  api,
  CreateOdpMonitoringRequest,
  NextOdpMonitoringDateResult,
  NextOdpMonitoringStageResult,
  OdpMonitoringDetailsResult,
  OdpMonitoringListResult,
  OdpMonitoringResult,
  OdpMonitoringNextDateOptionResult,
  UpdateOdpMonitoringRequest,
} from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';

type OdpMonitoringFollowupDto = {
  examinerId: number;
  id: number;
  date: string;
  trainerId?: number | null;
  idpTrainer?: string | null;
};

const odpMonitoringApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    odpMonitoringList: builder.query<OdpMonitoringListResult[], number>({
      query: (examinerId) => {
        return api.odpMonitoring.getAll({
          examinerId,
        });
      },
      providesTags: ['OdpMonitoring'],
    }),
    odpMonitoring: builder.query<OdpMonitoringDetailsResult, { id: number; examinerId: number }>({
      query: ({ id, examinerId }) => {
        return api.odpMonitoring.get({
          id,
          examinerId,
        });
      },
      providesTags: ['OdpMonitoring'],
    }),
    odpNextMonitoringDate: builder.query<NextOdpMonitoringDateResult, { examinerId: number }>({
      query: ({ examinerId }) => {
        return api.odpMonitoring.getNextMonitoringDate({
          examinerId,
        });
      },
      providesTags: ['OdpMonitoring'],
    }),
    calculateNextStage: builder.query<
      NextOdpMonitoringStageResult,
      { examinerId: number; date?: string }
    >({
      query: ({ examinerId, date }) => {
        return api.odpMonitoring.calculateNextStage({
          examinerId,
          date,
        });
      },
      providesTags: ['OdpMonitoring'],
    }),
    calculateNextMonitoringDate: builder.query<
      OdpMonitoringNextDateOptionResult[],
      { date: string; result: OdpMonitoringResult }
    >({
      query: ({ date, result }) => {
        return api.odpMonitoring.calculateNextMonitoringDate({
          date,
          result,
        });
      },
      providesTags: ['OdpMonitoring'],
    }),
    createOdpMonitoring: builder.mutation<
      OdpMonitoringDetailsResult,
      { examinerId: number; odpMonitoring: CreateOdpMonitoringRequest }
    >({
      query: ({ examinerId, odpMonitoring }) => {
        return api.odpMonitoring.create({ examinerId }, odpMonitoring);
      },
      invalidatesTags: ['OdpMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    updateOdpMonitoring: builder.mutation<
      void,
      { examinerId: number; id: number; odpMonitoring: UpdateOdpMonitoringRequest }
    >({
      query: ({ examinerId, id, odpMonitoring }) => {
        return api.odpMonitoring.update({ examinerId, id }, odpMonitoring);
      },
      invalidatesTags: ['OdpMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    deleteOdpMonitoring: builder.mutation<void, { id: number; examinerId: number }>({
      query: ({ id, examinerId }) => {
        return api.odpMonitoring.delete({
          examinerId,
          ids: [id],
        });
      },
      invalidatesTags: ['OdpMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    restoreOdpMonitoring: builder.mutation<void, { id: number; examinerId: number }>({
      query: ({ id, examinerId }) => {
        return api.odpMonitoring.restore({ id, examinerId });
      },
      invalidatesTags: ['OdpMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    bulkDeleteOdpMonitoring: builder.mutation<void, { ids: number[]; examinerId: number }>({
      query: (data) => {
        return api.odpMonitoring.delete(data);
      },
      invalidatesTags: ['OdpMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    checkCanEditCustomNextMonitoringDate: builder.query<
      boolean,
      { monitoringDate: string; monitoringId?: number; examinerId: number }
    >({
      query: ({ monitoringDate, monitoringId, examinerId }) => {
        return api.odpMonitoring.canEditCustomNextMonitoringDate({
          monitoringDate,
          monitoringId,
          examinerId,
        });
      },
      providesTags: ['OdpMonitoring'],
    }),
    canAddOdpFollowUp: builder.query<boolean, { examinerId: number; id: number }>({
      query: (params) => {
        return api.odpMonitoring.canAddFollowUp(params);
      },
      providesTags: ['OdpMonitoring'],
    }),
    addOdpFollowUp: builder.mutation<void, OdpMonitoringFollowupDto>({
      query: ({ examinerId, id, ...restParams }) => {
        return api.odpMonitoring.addFollowUp({ examinerId, id }, restParams);
      },
      invalidatesTags: ['OdpMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    updateOdpFollowUp: builder.mutation<void, OdpMonitoringFollowupDto>({
      query: ({ examinerId, id, ...restParams }) => {
        return api.odpMonitoring.updateFollowUp({ examinerId, id }, restParams);
      },
      invalidatesTags: ['OdpMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
    removeOdpFollowUp: builder.mutation<void, { examinerId: number; id: number }>({
      query: (params) => {
        return api.odpMonitoring.removeFollowUp(params);
      },
      invalidatesTags: ['OdpMonitoring', 'ExaminerStatus', 'Examiner'],
    }),
  }),
});

export const {
  useOdpMonitoringListQuery,
  useOdpMonitoringQuery,
  useOdpNextMonitoringDateQuery,
  useCalculateNextStageQuery,
  useCalculateNextMonitoringDateQuery,
  useCreateOdpMonitoringMutation,
  useUpdateOdpMonitoringMutation,
  useDeleteOdpMonitoringMutation,
  useRestoreOdpMonitoringMutation,
  useBulkDeleteOdpMonitoringMutation,
  useCheckCanEditCustomNextMonitoringDateQuery,
  useCanAddOdpFollowUpQuery,
  useAddOdpFollowUpMutation,
  useUpdateOdpFollowUpMutation,
  useRemoveOdpFollowUpMutation,
} = odpMonitoringApi;
